import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, ArrowUpRight, CalendarDays, Clock, PackageX,
  PiggyBank, Receipt, ScrollText, ShoppingCart, TrendingUp, Utensils, Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '../api/client';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, StatCard } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { CardSkeleton } from '../components/ui/Skeleton';
import { money, num, pct, relativeTime } from '../lib/format';
import { useAuth } from '../store/auth';

type Range = 'today' | 'week' | 'month';

interface Summary {
  range: Range;
  canSeeProfit: boolean;
  kpis: {
    sales: number; collected: number; orders: number; openOrders: number;
    averageOrder: number; cogs: number; profit: number; discounts: number;
    refunds: number; payables: number;
  };
  topItems: Array<{ name: string; qty: number; revenue: number }>;
  byType: Array<{ order_type: string; n: number; gross: number }>;
  tables: Array<{ status: string; n: number }>;
  lowStock: Array<{ name: string; quantity: number; unit: string; reorder: number }>;
  expiring: Array<{ name: string; batch_no: string; expiry: string; qty: number }>;
  recentActivity: Array<{ id: string; action: string; actor_name: string | null; created_at: string }>;
}

const RANGES: Array<{ value: Range; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const ORDER_TYPES: Record<string, string> = {
  dine_in: 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery', online: 'Online', qr: 'QR order',
};

export function DashboardPage() {
  const user = useAuth((s) => s.user);
  const can = useAuth((s) => s.can);
  const [range, setRange] = useState<Range>('today');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'summary', range],
    queryFn: () => http.get<Summary>('/dashboard/summary', { range }),
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-5">
      {/* Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink sm:text-2xl">
            {greeting}, {user?.fullName.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {user?.branchName ?? 'All branches'} · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-line bg-surface-1 p-0.5" role="group" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                aria-pressed={range === r.value}
                className={`min-h-touch rounded-md px-3 text-sm font-medium transition-colors ${
                  range === r.value ? 'bg-brand-600 text-white' : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {can('reports:sales') && (
            <Link to="/reports"><Button variant="secondary"><TrendingUp className="h-4 w-4" /> Reports</Button></Link>
          )}
        </div>
      </header>

      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      )}

      {data && (
        <>
          {/* KPI row */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Sales" value={money(data.kpis.sales)} icon={<Receipt className="h-4 w-4" />}
              hint={`${num(data.kpis.orders)} orders`}
              delta={`${num(data.kpis.openOrders)} still open`} deltaKind="flat"
            />
            <StatCard
              label="Average order" value={money(data.kpis.averageOrder)} icon={<ShoppingCart className="h-4 w-4" />}
              hint="incl. service charge"
            />
            <StatCard
              label="Gross profit"
              value={data.canSeeProfit ? money(data.kpis.profit) : '••••••'}
              icon={<PiggyBank className="h-4 w-4" />}
              hint={data.canSeeProfit ? `food cost ${money(data.kpis.cogs)}` : 'hidden for your role'}
              delta={data.canSeeProfit && data.kpis.sales > 0
                ? `${pct((data.kpis.profit / data.kpis.sales) * 100)} margin` : undefined}
              deltaKind="up"
            />
            <StatCard
              label="Outstanding dues" value={money(data.kpis.payables)} icon={<Wallet className="h-4 w-4" />}
              hint="supplier payables"
              delta={data.kpis.payables > 0 ? 'needs attention' : 'clear'}
              deltaKind={data.kpis.payables > 0 ? 'down' : 'up'}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Top sellers */}
            <Card className="lg:col-span-2">
              <CardHeader
                title="Top-selling items" subtitle={`By quantity · ${range}`}
                icon={<Activity className="h-4 w-4" />}
              />
              <CardBody>
                {data.topItems.length === 0 ? (
                  <EmptyState title="No sales in this period" message="Orders placed on the POS will appear here." />
                ) : (
                  <ul className="space-y-3">
                    {data.topItems.map((item) => {
                      const max = data.topItems[0].qty || 1;
                      return (
                        <li key={item.name}>
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="truncate font-medium text-ink">{item.name}</span>
                            <span className="shrink-0 tabular-nums text-ink-muted">
                              {num(item.qty)} × · {money(item.revenue)}
                            </span>
                          </div>
                          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-2">
                            <div
                              className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
                              style={{ width: `${(item.qty / max) * 100}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* Order mix */}
            <Card>
              <CardHeader title="Order mix" subtitle="By channel" icon={<Utensils className="h-4 w-4" />} />
              <CardBody>
                {data.byType.length === 0 ? (
                  <EmptyState title="Nothing yet" />
                ) : (
                  <ul className="space-y-2.5">
                    {data.byType.map((t) => {
                      const total = data.byType.reduce((s, x) => s + x.n, 0) || 1;
                      return (
                        <li key={t.order_type} className="flex items-center gap-3">
                          <span className="w-24 shrink-0 truncate text-sm text-ink-muted">
                            {ORDER_TYPES[t.order_type] ?? t.order_type}
                          </span>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                            <div className="h-full rounded-full bg-sky-500" style={{ width: `${(t.n / total) * 100}%` }} />
                          </div>
                          <span className="w-16 shrink-0 text-end text-sm tabular-nums text-ink">
                            {t.n} · {money(t.gross)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Live tables */}
            <Card>
              <CardHeader
                title="Live table status" subtitle="Floor right now"
                icon={<CalendarDays className="h-4 w-4" />}
                action={<Link to="/pos" className="text-xs font-medium text-brand-600 hover:underline">Floor map</Link>}
              />
              <CardBody>
                <TableStatusStrip tables={data.tables} />
              </CardBody>
            </Card>

            {/* Low stock */}
            <Card>
              <CardHeader
                title="Low stock" subtitle="At or below reorder level"
                icon={<PackageX className="h-4 w-4" />}
                action={<Link to="/inventory" className="text-xs font-medium text-brand-600 hover:underline">Inventory</Link>}
              />
              <CardBody>
                {data.lowStock.length === 0 ? (
                  <EmptyState title="Stock levels are healthy" message="Nothing is below its reorder level." />
                ) : (
                  <ul className="space-y-2">
                    {data.lowStock.map((s) => (
                      <li key={s.name} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-ink">{s.name}</span>
                        <span className="shrink-0 text-ink-muted">
                          <span className="font-medium text-red-600 dark:text-red-400">
                            {num(s.quantity, s.quantity < 10 ? 1 : 0)}
                          </span>
                          /{num(s.reorder)} {s.unit}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* Expiry */}
            <Card>
              <CardHeader title="Expiring soon" subtitle="Next 7 days" icon={<AlertTriangle className="h-4 w-4" />} />
              <CardBody>
                {data.expiring.length === 0 ? (
                  <EmptyState title="Nothing expiring" message="No batches fall due in the next week." />
                ) : (
                  <ul className="space-y-2">
                    {data.expiring.map((e) => (
                      <li key={`${e.name}-${e.batch_no}`} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-ink">{e.name}</span>
                        <Badge tone="warning">{e.batch_no} · {new Date(e.expiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Quick actions + activity */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader title="Quick actions" subtitle="Shortcuts for your role" icon={<Activity className="h-4 w-4" />} />
              <CardBody>
                <div className="grid grid-cols-2 gap-2">
                  <QuickAction to="/pos" label="Open POS" icon={<Utensils className="h-4 w-4" />} permission="pos:use" />
                  <QuickAction to="/kds" label="Kitchen" icon={<Clock className="h-4 w-4" />} permission="kds:view" />
                  <QuickAction to="/inventory" label="Stock" icon={<PackageX className="h-4 w-4" />} permission="inventory:view" />
                  <QuickAction to="/purchase" label="Purchase" icon={<ShoppingCart className="h-4 w-4" />} permission="purchase:view" />
                  <QuickAction to="/reports" label="Reports" icon={<TrendingUp className="h-4 w-4" />} permission="reports:sales" />
                  <QuickAction to="/audit" label="Audit log" icon={<ScrollText className="h-4 w-4" />} permission="audit:view" />
                </div>
              </CardBody>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader
                title="Recent activity" subtitle="From the audit trail"
                icon={<Activity className="h-4 w-4" />}
                action={can('audit:view') ? <Link to="/audit" className="text-xs font-medium text-brand-600 hover:underline">View all</Link> : undefined}
              />
              <CardBody className="p-0">
                {data.recentActivity.length === 0 ? (
                  <EmptyState title="No activity recorded yet" />
                ) : (
                  <ul className="divide-y divide-line">
                    {data.recentActivity.map((a) => (
                      <li key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                        <StatusBadge status={a.action.split('.')[1] ?? a.action} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-ink">{a.action}</span>
                          <span className="block truncate text-xs text-ink-faint">{a.actor_name ?? 'system'}</span>
                        </span>
                        <span className="shrink-0 text-xs text-ink-faint">{relativeTime(a.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function TableStatusStrip({ tables }: { tables: Array<{ status: string; n: number }> }) {
  const counts = Object.fromEntries(tables.map((t) => [t.status, t.n]));
  const total = tables.reduce((s, t) => s + t.n, 0) || 1;
  const order: Array<[string, string, string]> = [
    ['occupied', 'Occupied', 'bg-red-500'],
    ['reserved', 'Reserved', 'bg-amber-500'],
    ['billing', 'Billing', 'bg-violet-500'],
    ['dirty', 'To clean', 'bg-slate-400'],
    ['free', 'Free', 'bg-emerald-500'],
  ];
  return (
    <div className="space-y-3">
      <div className="flex h-3 overflow-hidden rounded-full bg-surface-2">
        {order.map(([key, , color]) => {
          const n = counts[key] ?? 0;
          return n > 0 ? <div key={key} className={color} style={{ width: `${(n / total) * 100}%` }} /> : null;
        })}
      </div>
      <ul className="grid grid-cols-2 gap-2 text-sm">
        {order.map(([key, label]) => (
          <li key={key} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${order.find((o) => o[0] === key)![2]}`} aria-hidden />
            <span className="text-ink-muted">{label}</span>
            <span className="ms-auto font-medium tabular-nums text-ink">{counts[key] ?? 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuickAction({ to, label, icon, permission }: { to: string; label: string; icon: React.ReactNode; permission: string }) {
  const can = useAuth((s) => s.can);
  if (!can(permission)) return null;
  return (
    <Link
      to={to}
      className="flex min-h-touch flex-col items-start gap-1.5 rounded-lg border border-line bg-surface-0 p-3 text-sm font-medium text-ink transition-colors hover:border-brand-400 hover:bg-surface-2"
    >
      <span className="text-brand-600 dark:text-brand-400">{icon}</span>
      {label}
      <ArrowUpRight className="ms-auto h-3.5 w-3.5 text-ink-faint" aria-hidden />
    </Link>
  );
}
